import { useState } from "react";
import { Info } from "lucide-react";

/** Circular score visual — 0..100. */
export function ScoreDial({ total, emoji, size = 100, strokeWidth = 8, showEmoji = true }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, total));
  const dash = (pct / 100) * circumference;

  const color =
    total >= 90 ? "#10B981" :   // emerald
    total >= 80 ? "#65B37B" :   // soft green
    total >= 70 ? "#F59E0B" :   // amber
    total >= 60 ? "#F97316" :   // orange
                   "#DC2626";    // red

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius}
                fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius}
                fill="none" stroke={color} strokeWidth={strokeWidth}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeLinecap="round"
                style={{ transition: "stroke-dasharray 0.7s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display leading-none" style={{ fontSize: size * 0.28, color }}>{total.toFixed(0)}</div>
        {showEmoji && <div className="text-xs mt-0.5">{emoji}</div>}
      </div>
    </div>
  );
}

const CLASS_LABEL = {
  excellent: "Excelente", very_good: "Muito Bom", improving: "A melhorar",
  insufficient: "Insuficiente", critical: "Crítico",
};

const CLASS_TEXT = {
  excellent: "text-[hsl(var(--success))]", very_good: "text-[hsl(var(--success))]",
  improving: "text-[hsl(var(--warning))]", insufficient: "text-[hsl(var(--warning))]", critical: "text-[hsl(var(--danger))]",
};

/** Compact bar-chart of the 9 score factors. */
export function ScoreBreakdown({ breakdown, blockers = [] }) {
  return (
    <div className="space-y-2" data-testid="score-breakdown">
      {blockers.length > 0 && (
        <div className="rounded-lg bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 p-3 text-xs mb-3" data-testid="score-blockers">
          <div className="font-semibold text-[hsl(var(--danger))] mb-1 uppercase tracking-[0.1em] text-[10px]">Regras de bloqueio aplicadas</div>
          <ul className="text-[hsl(var(--danger))] space-y-0.5">
            {blockers.map((b, i) => <li key={i}>• {b}</li>)}
          </ul>
        </div>
      )}
      {breakdown.map(f => {
        const w = Math.max(0, Math.min(100, f.score));
        return (
          <div key={f.factor} className="grid grid-cols-[1fr_auto] gap-2 items-center" data-testid={`factor-${f.factor}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-foreground font-medium truncate">{f.label}</span>
                {!f.measurable && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.1em] text-foreground font-semibold" title="Sem sinal — pontuação neutra">
                    <Info className="w-2.5 h-2.5" /> neutro
                  </span>
                )}
                <span className="text-[10px] text-muted-foreground ml-auto">peso {f.weight}%</span>
              </div>
              <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden mt-1">
                <div className="h-full rounded-full"
                     style={{
                       width: `${w}%`,
                       background: w >= 90 ? "#10B981" : w >= 70 ? "#F59E0B" : "#DC2626",
                       opacity: f.measurable ? 1 : 0.4,
                     }} />
              </div>
              {f.detail?.avg_minutes !== undefined && (
                <div className="text-[10px] text-muted-foreground mt-0.5">Média: {f.detail.avg_minutes} min ({f.detail.samples} amostras)</div>
              )}
              {f.detail?.total !== undefined && f.detail.same_day !== undefined && (
                <div className="text-[10px] text-muted-foreground mt-0.5">{f.detail.same_day} / {f.detail.total} no dia</div>
              )}
            </div>
            <div className="text-right">
              <div className="font-semibold text-sm" style={{ color: w >= 90 ? "#10B981" : w >= 70 ? "#F59E0B" : "#DC2626" }}>
                {f.score.toFixed(0)}
              </div>
              <div className="text-[10px] text-muted-foreground">→ {f.weighted_contribution?.toFixed(1)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Compact operator card: dial + name + classification + primary KPIs. */
export function OperatorScoreCard({ row, onClick, testid }) {
  const s = row.score || {};
  return (
    <button onClick={onClick}
            className="bg-card border border-border rounded-2xl p-5 hover:shadow-md hover:border-primary/40 transition-all text-left"
            data-testid={testid}>
      <div className="flex items-center gap-4">
        <ScoreDial total={s.total || 0} emoji={s.emoji || "🔴"} size={80} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate text-foreground">{row.operator_name}</div>
          <div className={`text-xs font-bold uppercase tracking-[0.15em] mt-0.5 ${CLASS_TEXT[s.classification] || "text-muted-foreground"}`}>
            {CLASS_LABEL[s.classification] || "—"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {row.counts?.leads_received || 0} leads · SLA {row.sla?.compliance_pct?.toFixed(0) || 0}% · {row.counts?.deals_closed || 0} negócios
          </div>
        </div>
      </div>
      {(s.blockers || []).length > 0 && (
        <div className="mt-2 text-[10px] text-[hsl(var(--danger))] flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          {s.blockers.length} regra{s.blockers.length === 1 ? "" : "s"} de bloqueio
        </div>
      )}
    </button>
  );
}
